import React from 'react';
import { Box, Typography, Grid, Paper, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';

const benefits = [
  {
    title: 'Earn Commission',
    description:
      'Add product reviews with your affiliate/buy link. When visitors purchase through it, you earn a commission – like Meesho resellers.',
  },
  {
    title: 'Showcase Your Style',
    description:
      'Upload real product photos, styling videos, and honest ratings to attract trust.',
  },
  {
    title: 'Grow Your Profile',
    description:
      'Build your own reviewer profile with followers and product success history.',
  },
  {
    title: 'Promote Products',
    description:
      'Help others decide what to buy and promote your favorite Amazon, Flipkart, or Meesho deals.',
  },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.2, duration: 0.4 },
  }),
};

const bottomSectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.8 } },
};

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ padding: { xs: 2, sm: 4 } }}>
      {/* Header Animation */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          gutterBottom
          fontWeight="bold"
          textAlign={isMobile ? 'center' : 'left'}
        >
          Welcome to Smart Product Reviews Platform
        </Typography>

        <Typography
          variant={isMobile ? 'body1' : 'subtitle1'}
          gutterBottom
          textAlign={isMobile ? 'center' : 'left'}
        >
          Share real product experiences with images, videos, and ratings. Add your buy link — help others, and earn like a reseller!
        </Typography>

        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          sx={{ mt: 4, mb: 2 }}
          textAlign={isMobile ? 'center' : 'left'}
        >
          💼 Reviewer / Reseller Benefits:
        </Typography>
      </motion.div>

      {/* Benefit Cards Animation */}
      <Grid container spacing={3}>
        {benefits.map((benefit, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <motion.div
              custom={index}
              initial="hidden"
              animate="visible"
              variants={cardVariants}
            >
              <Paper
                elevation={3}
                sx={{
                  padding: { xs: 2, sm: 2 },
                  borderRadius: 2,
                  height: '100%',
                  textAlign: isMobile ? 'center' : 'left',
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {benefit.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {benefit.description}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Bottom CTA Animation */}
      <motion.div variants={bottomSectionVariants} initial="hidden" animate="visible">
        <Box sx={{ mt: 5, textAlign: isMobile ? 'center' : 'left' }}>
          <Typography variant={isMobile ? 'body1' : 'h6'}>
            🔗 Ready to share your product experience?
          </Typography>
          <Typography variant="body1">
            Go to the Review Form, upload your product photos/videos, and add your shopping link.
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
};

export default HomePage;
